import { parseAsArrayOf, parseAsBoolean, parseAsNumberLiteral, parseAsString, parseAsStringLiteral, useQueryStates } from 'nuqs';
import { useMemo } from 'react';

import type { CommunityRegion } from '~/api/community/types';

import { communityRegions } from '~/api/community/constants';
import { availablePrimaryActivities } from '~/constants/constants';
import { SearchParamKeys } from '~/constants/url';

type PrimaryActivityId = (typeof availablePrimaryActivities)[number];

export const useCommunityProfileFilters = () => {
  const [{ activityIds, isOpenToWork, regions, search }, setFilters] = useQueryStates(
    {
      search: parseAsString.withDefault(''),
      activityIds: parseAsArrayOf(parseAsNumberLiteral(availablePrimaryActivities)).withDefault([]),
      isOpenToWork: parseAsBoolean.withDefault(false),
      regions: parseAsArrayOf(parseAsStringLiteral(communityRegions)).withDefault([]),
    },
    {
      urlKeys: {
        search: SearchParamKeys.FILTER_SEARCH,
        activityIds: SearchParamKeys.FILTER_ACTIVITY_ID,
        isOpenToWork: SearchParamKeys.FILTER_OPEN_TO_WORK,
        regions: SearchParamKeys.FILTER_REGION,
      },
    },
  );

  return useMemo(
    () => ({
      search: {
        value: search,
        onChange: (value: string) => setFilters({ search: value }),
      },
      activityIds: {
        value: [...new Set(activityIds)],
        onChange: (value: PrimaryActivityId[]) => setFilters({ activityIds: value }),
      },
      isOpenToWork: {
        value: isOpenToWork,
        onChange: (value: boolean) => setFilters({ isOpenToWork: value }),
      },
      regions: {
        value: [...new Set(regions)],
        onChange: (value: CommunityRegion[]) => setFilters({ regions: value }),
      },
    }),
    [activityIds, isOpenToWork, regions, search, setFilters],
  );
};
